import { DynamicStructuredTool, type StructuredToolInterface } from '@langchain/core/tools';
import type { RunnableConfig } from '@langchain/core/runnables';
import { z } from 'zod';
import { formatToolResult } from '../types.js';
import type { RunContext } from '../../agent/run-context.js';
import { isMcpConfigured, getMcpTools } from './drishti-mcp-client.js';
import { rankMcpTools } from './mcp-catalog.js';

export const SEARCH_TOOLS_DESCRIPTION = `Discover Drishti MCP tools relevant to the current task WITHOUT loading every MCP schema into context.

Use this tool for current or source-backed Indian-market research: symbol/company resolution, news, announcements, earnings, concalls, events, sector data, market movers, prices, volumes, documents, and daily summaries.

Appropriate MCP capabilities include resolve_symbols for ambiguous company names, get_symbols for known symbols, news, announcements, earnings, concalls, events, sectoral data, sector-wise company lists, top movers, top traded, 52-week highs/lows, price & volume time series, historical price/volume since a date, document search/fetch, market daily summaries, and research-prompt templates.

Do not call resolve_symbols for a known Indian listed symbol unless there is real ambiguity. Pass a short description of what you need (e.g. "resolve symbol for JSW steel", "latest announcements for TCS", "top gaining stocks today", "52 week highs", "price and volume history for RELIANCE"). It returns a small set of matching MCP tools, which then become directly callable on your next step.`;

/**
 * Push already-loaded MCP tools onto the run context so the agent loop binds
 * them to the LLM on the next iteration. Deduped by tool name. Synchronous, so
 * there is no window where the model could call a tool before it is bound.
 */
function activate(ctx: RunContext, tools: StructuredToolInterface[]): void {
  const already = new Set(ctx.activeMcpTools.map((t) => t.name));
  for (const tool of tools) {
    if (!already.has(tool.name)) {
      ctx.activeMcpTools.push(tool);
      already.add(tool.name);
    }
  }
}

/**
 * The `search_tools` tool. Ranks the MCP catalog against the task and activates
 * the top matches into the run context (via config.metadata.runContext). It is
 * only registered when MCP is configured.
 */
export function createSearchToolsTool(): DynamicStructuredTool {
  return new DynamicStructuredTool({
    name: 'search_tools',
    description: SEARCH_TOOLS_DESCRIPTION,
    schema: z.object({
      query: z.string().describe('Short description of the capability you need.'),
      limit: z.number().int().min(1).max(10).default(5).describe('Max MCP tools to return.'),
    }),
    func: async (input, _runManager, config?: RunnableConfig) => {
      if (!isMcpConfigured()) {
        return formatToolResult({
          available: false,
          message:
            'The Drishti MCP capability layer is not configured.',
        });
      }

      const matches = rankMcpTools(input.query, input.limit ?? 5);
      if (matches.length === 0) {
        return formatToolResult({
          available: true,
          matches: [],
          message:
            'No MCP tools matched this query. Rephrase the capability you need.',
        });
      }

      // Load real MCP tools and activate the matched ones for the next turn.
      const loaded = await getMcpTools();
      const loadedByName = new Map(loaded.map((t) => [t.name, t]));
      const activatable = matches
        .map((m) => loadedByName.get(m.name))
        .filter((t): t is StructuredToolInterface => Boolean(t));

      const ctx = config?.metadata?.runContext as RunContext | undefined;
      if (ctx && activatable.length > 0) {
        activate(ctx, activatable);
      }

      return formatToolResult({
        available: true,
        note: 'These MCP tools are now callable directly on your next step.',
        matches: matches.map((m) => {
          const tool = loadedByName.get(m.name);
          const params = tool ? extractParamNames(tool.schema) : [];
          return {
            name: m.name,
            summary: m.summary,
            category: m.category,
            params,
            loaded: loadedByName.has(m.name),
          };
        }),
      });
    },
  });
}

/** Best-effort extraction of top-level parameter names from a tool schema. */
function extractParamNames(schema: unknown): string[] {
  if (!schema || typeof schema !== 'object') return [];
  // zod object
  const shape = (schema as { shape?: Record<string, unknown> }).shape;
  if (shape && typeof shape === 'object') return Object.keys(shape);
  // JSON schema
  const props = (schema as { properties?: Record<string, unknown> }).properties;
  if (props && typeof props === 'object') return Object.keys(props);
  return [];
}
